import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import businessFlowsTheme from "../../theme/businessFlowsTheme";
// The animated BPMN card the panel drew until 28 Sep 2026, one line from
// coming back now that the network picture is what a reader sees.
// import ProcessCanvas from "./ProcessCanvas";
import AuthNetwork from "./AuthNetwork";
import useBranding, { DEFAULT_BRANDING } from "../../utils/branding";
import "./auth.css";

const HIGHLIGHTS = [
  "BPMN reference processes for every end-to-end flow",
  "See how processes, systems and roles connect",
  "Process-driven SAP S/4HANA cookbook",
];

// Which skin the brand panel wears: "light" (the tinted canvas the picture was
// drawn for) or "ink" (the deep navy it wore before). Both live in auth.css.
export const PANEL_SKIN = "light";

// The form keeps a readable measure of its own instead of half the monitor;
// the panel takes the rest, because it is the half carrying the picture.
const FORM_COLUMN = "clamp(390px, 38%, 560px)";

// Signed-out pages: the brand's story on one side, the form on the other.
export default function AuthLayout({ children, maxWidth = 420 }) {
  const branding = useBranding();
  const footerLinks = [
    { label: "Privacy", href: branding.privacyUrl },
    { label: "Terms", href: branding.termsUrl },
    { label: "Impressum", href: branding.imprintUrl },
  ];
  const ownColour = branding.primaryColour !== DEFAULT_BRANDING.primaryColour;
  return (
    <ThemeProvider theme={businessFlowsTheme}>
      <Box
        className="bfx"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: "100%",
          minHeight: "100%",
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <Box
          component="aside"
          aria-label={`About ${branding.productName}`}
          className={`bfx-brand${PANEL_SKIN === "ink" ? " bfx-brand--ink" : ""}`}
          sx={{
            display: "flex",
            flex: { xs: "0 0 auto", md: "1 1 auto" },
            flexDirection: "column",
            minWidth: 0,
            color: "var(--bfx-fg)",
            position: "relative",
            overflow: "hidden",
          }}
          // An admin's own brand colour repaints the panel; the picture keeps
          // its own colours, which are the artwork's.
          style={ownColour ? { backgroundColor: branding.primaryColour, backgroundImage: "none", "--bfx-fg": "#fff", "--bfx-fg-soft": "rgba(255,255,255,0.74)", "--bfx-fg-quiet": "rgba(255,255,255,0.5)", "--bfx-tick": "#f0e631", "--bfx-pulse": "#ffffff" } : undefined}
        >
          {/* One composition top to bottom: rule, headline, paragraph, picture,
              proof lines - centred as a block, with the picture taking whatever
              room the rest leaves rather than a size of its own. */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: { md: 700, xl: 880 },
              mx: "auto",
              flex: { xs: "0 0 auto", md: "1 1 auto" },
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {/* The brand's yellow marks the start of the thought, as it does
                on Home. It is the accent; nothing else is painted yellow. */}
            <Box
              aria-hidden="true"
              sx={{ width: 34, height: 3, borderRadius: 2, bgcolor: "var(--bfx-rule)", mb: { xs: 1.25, md: 2 } }}
            />
            <Typography
              variant="h2"
              component="p"
              sx={{
                fontSize: { xs: "1.15rem", sm: "1.6rem", md: "2rem", lg: "2.45rem" },
                color: "var(--bfx-fg)",
                maxWidth: "20ch",
                overflowWrap: "anywhere",
              }}
            >
              Every end-to-end process, modelled and ready to explore.
            </Typography>
            <Typography
              sx={{
                display: { xs: "none", sm: "block" },
                color: "var(--bfx-fg-soft)",
                fontSize: "1.02rem",
                lineHeight: 1.55,
                mt: 1.75,
                maxWidth: "54ch",
              }}
            >
              Business Flows gives transformation teams a living library of reference processes, so you start
              from proven structure instead of a blank page.
            </Typography>

            {/* Decorative beside the words above, so it is not described twice. */}
            <Box className="bfx-figure" sx={{ mt: { xs: 2.5, md: 3 } }}>
              <AuthNetwork />
            </Box>

            <Stack
              spacing={1.25}
              sx={{ display: { xs: "none", md: "flex" }, mt: 3, flexShrink: 0 }}
              className="bfx-brand__highlights"
            >
              {HIGHLIGHTS.map((item) => (
                <Stack key={item} direction="row" spacing={1.25} alignItems="flex-start">
                  <CheckCircleRoundedIcon sx={{ color: "var(--bfx-tick)", fontSize: 19, mt: "2px", flexShrink: 0 }} />
                  <Typography sx={{ color: "var(--bfx-fg-soft)", fontSize: "0.94rem", lineHeight: 1.45 }}>
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Typography
            sx={{ display: { xs: "none", md: "block" }, color: "var(--bfx-fg-quiet)", fontSize: "0.8rem", mt: 2.5, flexShrink: 0 }}
          >
            © {new Date().getFullYear()} {branding.companyName}
          </Typography>
        </Box>

        <Box
          component="main"
          className="bfx-form bf-quiet-scroll"
          sx={{
            flex: { xs: "1 1 auto", md: `0 0 ${FORM_COLUMN}` },
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            minHeight: 0,
            bgcolor: "background.paper",
            overflowY: "auto",
          }}
        >
          {/* The wordmark belongs to the form, not to the top of the column:
              mark, heading, fields and the line under them are one block, and
              the block is what is centred. Only the footer is pinned. */}
          <Box
            sx={{
              // Grows to centre the block, never shrinks under it: on a short
              // window the column scrolls rather than the block overlapping
              // the footer.
              flex: "1 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 3, sm: 5 },
              py: { xs: 3, md: 4 },
            }}
          >
            <Box sx={{ width: "100%", maxWidth }}>
              <Box
                component={RouterLink}
                to="/login"
                aria-label={`${branding.productName} home`}
                sx={{ display: "inline-flex", mb: { xs: 3, md: 3.5 } }}
              >
                <Box component="img" src={branding.logoUrl} alt={branding.productName} sx={{ height: { xs: 28, md: 32 } }} />
              </Box>
              {children}
            </Box>
          </Box>

          <Stack
            direction="row"
            spacing={2.5}
            justifyContent="center"
            sx={{ pb: 2, px: 2, flexWrap: "wrap", rowGap: 0.75, flexShrink: 0 }}
          >
            {footerLinks.map((l) => (
              <Link
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.82rem" }}
              >
                {l.label}
              </Link>
            ))}
            <Link component={RouterLink} to="/release-notes" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.82rem" }}>
              What's new
            </Link>
          </Stack>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
